import { createSelector } from '@reduxjs/toolkit';
import {
  getFromImmutableStateCompatible,
  getImmutableCompatible,
} from 'src/utils/immutableUtils';
import { name } from './slice';
import type { IHotelsState } from './types';

export const _hotelsSelector = (state) => getImmutableCompatible(state, name);
export const hotelsSelector =
  getFromImmutableStateCompatible<IHotelsState>(_hotelsSelector);

export const getHotelsList = createSelector(
  hotelsSelector,
  (state) => state.hotels,
);

export const getHotelsIds = createSelector(getHotelsList, (hotels) =>
  hotels.map((hotelItem) => hotelItem.hotel.id),
);

export const getHotelsSurchargesByLevel = createSelector(
  getHotelsList,
  (hotels) => hotels.map((hotelItem) => hotelItem.surcharges_by_level),
);

export const getHotelsAccommodationAvailability = createSelector(
  getHotelsList,
  (hotels) => hotels.map((hotel) => hotel.availability.hotel),
);

export const getHotelsFlightSeats = createSelector(getHotelsList, (hotels) =>
  hotels.map((hotel) => hotel.availability.flight),
);

export const getHotelsTourIds = createSelector(getHotelsList, (hotels) =>
  hotels.map((hotel) => hotel.tour_id),
);

export const getFilteredHotelsList = createSelector(
  hotelsSelector,
  (state) => state.filtered_hotels,
);

export const getCurrentVisibleHotels = createSelector(
  hotelsSelector,
  (state) => state.currentVisibleHotels,
);

export const getHotelsCount = createSelector(
  hotelsSelector,
  (state) => state.hotelsCount,
);
export const getCurrentPage = createSelector(
  hotelsSelector,
  (state) => state.currentPage,
);

export const getAwaitingPagination = createSelector(
  hotelsSelector,
  (state) => state.awaitingPagination,
);

export const getHasHotels = createSelector(
  getHotelsList,
  (hotels) => hotels?.length > 0 || false,
);

export const getHasHotelFilteredHotels = createSelector(
  [getHotelsList, getFilteredHotelsList],
  (hotels, filtered_hotels) => !hotels?.length && !filtered_hotels?.length,
);

export const latestVisibleHotelSelector = createSelector(
  getCurrentVisibleHotels,
  (visibleHotels) =>
    visibleHotels?.length ? Math.max(...visibleHotels) : null,
);

export const getHotelById = createSelector(
  [getHotelsList, (_, id) => id],
  (hotels, id) => hotels.find((hotel) => hotel.hotel.id === id),
);

export const getFallbackSearch = createSelector(
  hotelsSelector,
  (state) => state.fallbackSearch,
);
export const getNearestDepartures = createSelector(
  hotelsSelector,
  (state) => state.nearestDepartures,
);

export const getNearestDates = createSelector(
  hotelsSelector,
  (state) => state.nearestDates,
);

export const getPageLimit = createSelector(
  hotelsSelector,
  (state) => state.pageLimit,
);

export const getLiveSearchAllowed = createSelector(
  hotelsSelector,
  (state) => state.liveSearchAllowed,
);
